import { Injectable } from '@angular/core';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { CoreItemService } from '../core-item/core-item.service';

import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { CoreItemType } from '../core-item/core-item';
import { PostHogEvent } from '../posthog/posthog.event';
import { Methodology, MethodologySubData } from './methodology';

@Injectable({
  providedIn: 'root',
})
export class MethodologyService {
  private _changeSubject = new BehaviorSubject<Methodology[] | Methodology | undefined>(undefined);

  constructor(private coreItemService: CoreItemService) {}

  getMethodologyById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Methodology));
  }

  methodologyList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({ coreTypeList: [CoreItemType.methodology] }).pipe(
          map((list) => {
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
            return list as Methodology[];
          })
        );
      })
    );
  }

  updateMethodology$(methodology: Methodology) {
    return this.coreItemService.updateCoreItem$(methodology);
  }

  addMethodologyList$(dataList: { title: string; description: string; subData: MethodologySubData }[]) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: dataList.map((c) => ({
          title: c.title,
          description: c.description,
          coreType: CoreItemType.methodology,
          subData: c.subData,
        })),
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr as Methodology[]);
          return arr;
        })
      );
  }

  addMethodology$(title: string, description: string, subData: MethodologySubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            description,
            coreType: CoreItemType.methodology,
            subData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as Methodology);
          return arr[0] as Methodology;
        })
      );
  }

  deleteMethodologyList$(methodologyList: Methodology[]) {
    const deleteMetadataList = methodologyList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.methodologyDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }
}
